import { t } from "i18next";
import { ComponentProps } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { Artwork, ArtworkHistory } from "../types";
import { DimensionsFormatting, isValidDimField } from "./shared/dimensions";
import { InscriptionDosCF } from "./shared/inscription-dos";
import { LocalisationCF } from "./shared/localisation";
import { RelatedAuthorsListCF } from "./shared/related-authors";
import { SignatureCF } from "./shared/signature";
import { SupportTechniqueCF } from "./shared/technique";
import {
  CreditCF,
  DateCF,
  MainTitleCF,
  TitleCF,
  TitlePhotoMasterCF,
} from "./shared/title";
import { SeparatedList } from "./shared/utils";

/**
 * Vérifie si un champ JSON ou string est rempli (non vide)
 */
const isFieldFilled = (field: any): boolean => {
  if (!field) return false;
  if (typeof field === "string") {
    return field.trim().length > 0;
  }
  if (typeof field === "object") {
    // Pour LocaleDict ou objets JSON, vérifier qu'au moins une valeur existe et n'est pas vide
    return Object.values(field).some(
      (value) => typeof value === "string" && value.trim().length > 0,
    );
  }
  return false;
};

export const ArtworkDimensionsCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionDimensionsOeuvre"
    | "oeuvresDescriptionDimensionsOeuvreRaw"
    | "oeuvresDescriptionDimensionsOeuvreInconnues"
  >
> = ({ input, ...props }) => {
  return (
    <DimensionsFormatting
      input={{
        ...input.oeuvresDescriptionDimensionsOeuvre,
        rawDimensions: input.oeuvresDescriptionDimensionsOeuvreRaw,
        unknownDimensions: input.oeuvresDescriptionDimensionsOeuvreInconnues,
      }}
      {...props}
    />
  );
};

export const ArtworkDimensionsHorsMargesCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionDimensionsHorsMarges"
    | "oeuvresDescriptionDimensionsHorsMargesInconnues"
  >
> = ({ input, ...props }) => {
  return (
    <DimensionsFormatting
      {...props}
      input={{
        ...input.oeuvresDescriptionDimensionsHorsMarges,
        unknownDimensions:
          input.oeuvresDescriptionDimensionsHorsMargesInconnues,
      }}
    />
  );
};

export const ArtworkDimensionsCadreCF: ComputeFieldComponent<
  Pick<Artwork, "oeuvresRegieDimensionsCadre">
> = ({ input, ...props }) => (
  <DimensionsFormatting
    input={{ ...input.oeuvresRegieDimensionsCadre }}
    {...props}
  />
);

export const ArtworkRelatedAuthorsCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "relatedAuthor"
    | "oeuvresDescriptionNomArtisteAnonyme"
    | "oeuvresDescriptionNomArtisteDApres"
    | "oeuvresDescriptionNomArtisteAttribueA"
    | "oeuvresDescriptionNomArtisteDansLeGoutDe"
    | "oeuvresDescriptionNomArtisteEntourageDe"
    | "oeuvresDescriptionNomArtisteAtelierDe"
  >
> = ({ input, ...props }) => (
  <RelatedAuthorsListCF
    input={{
      relatedAuthors: input.relatedAuthor,
      attributedTo: input.oeuvresDescriptionNomArtisteAttribueA,
      from: input.oeuvresDescriptionNomArtisteDApres,
      tasteOf: input.oeuvresDescriptionNomArtisteDansLeGoutDe,
      entourageOf: input.oeuvresDescriptionNomArtisteEntourageDe,
      anonymous: input.oeuvresDescriptionNomArtisteAnonyme,
      studioOf: input.oeuvresDescriptionNomArtisteAtelierDe,
    }}
    {...props}
  />
);

export const ArtworkSupportTechniqueCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTechnique"
    | "oeuvresDescriptionSupport"
    | "oeuvresDescriptionOeuvreDoubleFace"
  > & {
    preposition?: string;
    doubleFace?: string;
  }
> = ({ input, ...props }) => (
  <SupportTechniqueCF
    input={{
      ...input,
      technique: input.oeuvresDescriptionTechnique,
      support: input.oeuvresDescriptionSupport,
      isDoubleSided: input.oeuvresDescriptionOeuvreDoubleFace,
    }}
    {...props}
  />
);

export const ArtworkSignatureCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionSignatureLocalisation"
    | "oeuvresDescriptionSignatureTranscription"
  >
> = ({ input, ...props }) => (
  <SignatureCF
    input={{
      location: input.oeuvresDescriptionSignatureLocalisation,
      transcription: input.oeuvresDescriptionSignatureTranscription,
    }}
    {...props}
  />
);

export const ArtworkInscriptionDosCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionInscriptionsDosMultilingual"
    | "oeuvresDescriptionInscriptionsDosPublished"
  >
> = ({ input, inline = false, ...props }) => (
  <InscriptionDosCF
    input={{
      published: input.oeuvresDescriptionInscriptionsDosPublished,
      inscription: input.oeuvresDescriptionInscriptionsDosMultilingual,
    }}
    inline={inline}
    {...props}
  />
);

export const ArtworkLocalisationCF: ComputeFieldComponent<
  Pick<Artwork, "oeuvresLocalisationHistoriqueCollectionCatalogue">
> = ({ input, ...props }) => (
  <LocalisationCF
    input={{
      localisation: input.oeuvresLocalisationHistoriqueCollectionCatalogue,
    }}
    {...props}
  />
);

export const ArtworkMainTitleCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTitreInconnu"
    | "oeuvresDescriptionTitreSansTitre"
    | "oeuvresValidationTitleNotConfirmed"
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreation"
  >
> = ({ input, ...props }) => (
  <MainTitleCF
    input={{
      title: input.oeuvresDescriptionTitre,
      titleState: {
        notConfirmed: input.oeuvresValidationTitleNotConfirmed,
        none: input.oeuvresDescriptionTitreSansTitre,
        unknown: input.oeuvresDescriptionTitreInconnu,
      },
    }}
    {...props}
  />
);

export const ArtworkDateCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreation"
  >
> = ({ input, ...props }) => (
  <DateCF
    input={{
      date: input.oeuvresDescriptionDateDeCreation,
      unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
    }}
    {...props}
  />
);

export const ArtworkTitleCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTitreSansTitre"
    | "oeuvresDescriptionTitreInconnu"
    | "oeuvresValidationTitleNotConfirmed"
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreation"
    | "oeuvresDescriptionSousTitre"
    | "oeuvresDescriptionAutreTitre"
  > & {
    showDate?: boolean;
  }
> = ({ input, ...props }) => (
  <TitleCF
    input={{
      title: input.oeuvresDescriptionTitre,
      titleState: {
        notConfirmed: input.oeuvresValidationTitleNotConfirmed,
        none: input.oeuvresDescriptionTitreSansTitre,
        unknown: input.oeuvresDescriptionTitreInconnu,
      },
      subtitle: input.oeuvresDescriptionSousTitre,
      otherTitle: input.oeuvresDescriptionAutreTitre,

      date: input.oeuvresDescriptionDateDeCreation,
      unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      showDate: input.showDate,
    }}
    {...props}
  />
);

export const UnstyledArtworkTitleCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkTitleCF>["input"]
> = ({ input, ...props }) => (
  <MainTitleCF
    {...props}
    input={{
      styled: false,
      title: input.oeuvresDescriptionTitre,
      titleState: {
        notConfirmed: input.oeuvresValidationTitleNotConfirmed,
        none: input.oeuvresDescriptionTitreSansTitre,
        unknown: input.oeuvresDescriptionTitreInconnu,
      },
    }}
  />
);

export const ArtworkSummaryCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkRelatedAuthorsCF>["input"] &
    ComponentProps<typeof ArtworkTitleCF>["input"] &
    ComponentProps<typeof ArtworkDimensionsCF>["input"] &
    ComponentProps<typeof ArtworkSupportTechniqueCF>["input"] &
    ComponentProps<typeof ArtworkSignatureCF>["input"] &
    ComponentProps<typeof ArtworkInscriptionDosCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"]
> = ({ input, lineBreak = true, ...props }) => {
  return (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={[
          <ArtworkRelatedAuthorsCF input={input} {...props} />,
          <ArtworkTitleCF input={input} {...props} />,
          <ArtworkDimensionsCF input={input} {...props} />,
          <ArtworkSupportTechniqueCF input={input} {...props} />,
          <ArtworkSignatureCF input={input} {...props} />,
          <ArtworkInscriptionDosCF input={input} {...props} />,
          <ArtworkLocalisationCF input={input} {...props} />,
        ]}
      />
    </>
  );
};

export const RelatedEnsembleDescriptionCF: ComputeFieldComponent<
  Pick<Artwork, "relatedEnsemble">
> = ({ input, lineBreak = false }) => {
  const ensembleNames = input.relatedEnsemble
    .map((item) => item.ensemblesDescriptionNomEnsemble)
    .join(", ");

  return (
    <>
      {ensembleNames}
      {lineBreak && <br />}
    </>
  );
};

export const ArtworkDescriptionEnsembleFixeCF: ComputeFieldComponent<
  Pick<Artwork, "relatedEnsemble">
> = ({ input, ...props }) => {
  const ensembleList = input.relatedEnsemble.filter(
    (i) => i.ensemblesDescriptionType === "fixe",
  );

  return (
    <RelatedEnsembleDescriptionCF
      input={{ relatedEnsemble: ensembleList }}
      {...props}
    />
  );
};

export const ArtworkDescriptionEnsembleTemporaireCf: ComputeFieldComponent<
  Pick<Artwork, "relatedEnsemble">
> = ({ input, ...props }) => {
  const ensembleList = input.relatedEnsemble.filter(
    (i) => i.ensemblesDescriptionType === "temporaire",
  );

  return (
    <RelatedEnsembleDescriptionCF
      input={{ relatedEnsemble: ensembleList }}
      {...props}
    />
  );
};

export const ArtworkHistoryDescriptionCF: ComputeFieldComponent<
  Pick<
    ArtworkHistory,
    | "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere"
    | "oeuvresLocalisationHistoriqueHistoriqueNom"
    | "oeuvresLocalisationHistoriqueHistoriqueVille"
  >
> = ({ input }) => {
  const location =
    input.oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere
      ? t("computeField.collectionParticuliere")
      : input.oeuvresLocalisationHistoriqueHistoriqueNom;

  const city = input.oeuvresLocalisationHistoriqueHistoriqueVille?.trim()
    ? `${location != undefined ? ", " : ""}${input.oeuvresLocalisationHistoriqueHistoriqueVille}`
    : "";

  return (
    <>
      {location ?? ""}
      {city}
    </>
  );
};

export const ArtworkLocalisationHistoriqueCF: ComputeFieldComponent<{
  artworkHistory?: ComponentProps<
    typeof ArtworkHistoryDescriptionCF
  >["input"][];
}> = ({ input, lineBreak = true, ...props }) => {
  if (!input.artworkHistory || input.artworkHistory.length === 0) {
    return <></>;
  }

  return (
    <SeparatedList
      {...props}
      input={input.artworkHistory.map((history, index) => (
        <ArtworkHistoryDescriptionCF key={index} input={history} {...props} />
      ))}
      lineBreak={lineBreak}
    />
  );
};

/*
 * PHOTOGRAPHY MASTER CF
 */
export const ArtworkTitlePhotoMasterCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTitreSansTitre"
    | "oeuvresDescriptionTitreInconnu"
    | "oeuvresValidationTitleNotConfirmed"
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreation"
    | "oeuvresDescriptionLieuDeCreation"
  > & {
    showDate?: boolean;
  }
> = ({ input, ...props }) => (
  <TitlePhotoMasterCF
    input={{
      title: input.oeuvresDescriptionTitre,
      titleState: {
        notConfirmed: input.oeuvresValidationTitleNotConfirmed,
        none: input.oeuvresDescriptionTitreSansTitre,
        unknown: input.oeuvresDescriptionTitreInconnu,
      },

      location: input.oeuvresDescriptionLieuDeCreation,
      date: input.oeuvresDescriptionDateDeCreation,
      unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      showDate: input.showDate,
    }}
    {...props}
  />
);

export const ArtworkCreditOeuvreCF: ComputeFieldComponent<
  Pick<Artwork, "oeuvresCreditsCreditOeuvre">
> = ({ input, ...props }) => (
  <CreditCF input={{ credit: input.oeuvresCreditsCreditOeuvre }} {...props} />
);

export const ArtworkSummaryPhotographieMasterCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkRelatedAuthorsCF>["input"] &
    ComponentProps<typeof ArtworkTitlePhotoMasterCF>["input"] &
    ComponentProps<typeof ArtworkCreditOeuvreCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"] &
    ComponentProps<typeof ArtworkDimensionsCF>["input"] &
    ComponentProps<typeof ArtworkSupportTechniqueCF>["input"] &
    ComponentProps<typeof ArtworkSignatureCF>["input"] &
    ComponentProps<typeof ArtworkInscriptionDosCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"]
> = ({ input, lineBreak = true, ...props }) => {
  const dimensions = input.oeuvresDescriptionDimensionsOeuvre;

  const invalidDimensions =
    dimensions &&
    !isValidDimField(dimensions.cm_width) &&
    !isValidDimField(dimensions.cm_height) &&
    !isValidDimField(dimensions.cm_depth) &&
    !isValidDimField(dimensions.cm_diameter);

  const CFWithoutDimensions = (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={[
          <ArtworkRelatedAuthorsCF input={input} {...props} />,
          <ArtworkTitlePhotoMasterCF input={input} {...props} />,
          <ArtworkCreditOeuvreCF input={input} {...props} />,
        ]}
      />
    </>
  );

  if (
    (!dimensions || invalidDimensions) &&
    !input.oeuvresDescriptionDimensionsOeuvreRaw
  ) {
    return CFWithoutDimensions;
  }

  return (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={[
          <ArtworkRelatedAuthorsCF input={input} {...props} />,
          <ArtworkTitlePhotoMasterCF input={input} {...props} />,
          <ArtworkSupportTechniqueCF input={input} {...props} />,
          <ArtworkDimensionsCF input={input} {...props} />,
          <SeparatedList
            {...props}
            lineBreak={false}
            input={[
              <ArtworkSignatureCF input={input} {...props} />,
              <ArtworkInscriptionDosCF
                input={input}
                inline={
                  isFieldFilled(
                    input.oeuvresDescriptionSignatureTranscription,
                  ) ||
                  isFieldFilled(input.oeuvresDescriptionSignatureLocalisation)
                }
                {...props}
              />,
            ]}
          />,
          <ArtworkLocalisationCF input={input} {...props} />,
          <ArtworkCreditOeuvreCF input={input} {...props} />,
        ]}
      />
    </>
  );
};

export const ArtworkOverviewSummaryCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkDateCF>["input"] &
    ComponentProps<typeof ArtworkTitlePhotoMasterCF>["input"] &
    ComponentProps<typeof ArtworkSupportTechniqueCF>["input"] &
    ComponentProps<typeof ArtworkDimensionsCF>["input"] &
    ComponentProps<typeof ArtworkSignatureCF>["input"] &
    ComponentProps<typeof ArtworkInscriptionDosCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"] &
    ComponentProps<typeof ArtworkCreditOeuvreCF>["input"] & {
      preposition?: string;
      doubleFace?: string;
    },
  {
    isPhotoModuleEnabled: boolean;
    hasValidDimensions: boolean;
  }
> = ({
  input,
  lineBreak = true,
  isPhotoModuleEnabled,
  hasValidDimensions,
  ...props
}) => {
  // Cas 1 : Pas de module photo
  if (!isPhotoModuleEnabled) {
    return (
      <>
        <SeparatedList
          {...props}
          lineBreak={lineBreak}
          input={[
            <DateCF
              input={{
                date: input.oeuvresDescriptionDateDeCreation,
                unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
              }}
              {...props}
            />,
            <ArtworkSupportTechniqueCF input={input} {...props} />,
            <ArtworkDimensionsCF input={input} {...props} />,
            <ArtworkSignatureCF input={input} {...props} />,
            <ArtworkInscriptionDosCF input={input} {...props} />,
            <ArtworkLocalisationCF input={input} {...props} />,
          ]}
        />
      </>
    );
  }

  // Cas 2 : Module photo sans dimensions
  if (!hasValidDimensions) {
    return (
      <>
        <SeparatedList
          {...props}
          lineBreak={lineBreak}
          input={[
            <SeparatedList
              {...props}
              lineBreak={false}
              input={[
                input.oeuvresDescriptionLieuDeCreation,
                <DateCF
                  input={{
                    date: input.oeuvresDescriptionDateDeCreation,
                    unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
                  }}
                  {...props}
                />,
              ]}
            />,
            <ArtworkLocalisationCF input={input} {...props} />,
            <ArtworkCreditOeuvreCF input={input} {...props} />,
          ]}
        />
      </>
    );
  }

  // Cas 3 : Module photo avec dimensions
  return (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={[
          <SeparatedList
            {...props}
            lineBreak={false}
            input={[
              input.oeuvresDescriptionLieuDeCreation,
              <DateCF
                input={{
                  date: input.oeuvresDescriptionDateDeCreation,
                  unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
                }}
                {...props}
              />,
            ]}
          />,
          <SupportTechniqueCF
            input={{
              technique: input.oeuvresDescriptionTechnique,
              support: input.oeuvresDescriptionSupport,
              isDoubleSided: input.oeuvresDescriptionOeuvreDoubleFace,
              preposition: input.preposition,
              doubleFace: input.doubleFace,
            }}
            {...props}
          />,
          <ArtworkDimensionsCF input={input} {...props} />,
          <SeparatedList
            {...props}
            lineBreak={false}
            input={[
              <ArtworkSignatureCF input={input} {...props} />,
              <ArtworkInscriptionDosCF
                input={input}
                inline={
                  isFieldFilled(
                    input.oeuvresDescriptionSignatureTranscription,
                  ) ||
                  isFieldFilled(input.oeuvresDescriptionSignatureLocalisation)
                }
                {...props}
              />,
            ]}
          />,
          <ArtworkLocalisationCF input={input} {...props} />,
          <ArtworkCreditOeuvreCF input={input} {...props} />,
        ]}
      />
    </>
  );
};
